import React, { useMemo } from 'react';
import moment from 'moment';

import { CreatorContainer } from '../../../containers';
import { Text } from '../../../components';
import { ActionDock, EmptyState, Screen } from '../../../design/components';
import SetupSection from '../components/SetupSection';
import SetupCreatorPlacePhotos from '../components/SetupCreatorPlacePhotos';
import SetupInfoRow from '../components/SetupInfoRow';
import SetupTextField from '../components/SetupTextField';
import { MachineSelector, PlaceSelector, PlanogramSelector } from '../../../components/selector';
import { useThemedStyles } from '../../../hooks/useThemedStyles';
import { POS_TYPE } from '../../../../common/constants';
import SetupAssigneePicker from '../components/SetupAssigneePicker';

const MarketSetupCreator = ({
	locationSetup,
	values,
	changeFormValue,
	onSubmit,
	submitting,
	errors,
}) => {
	const styles = useThemedStyles(createStyles);

	const placeFilter = useMemo(() => {
		return locationSetup?.location?.id ? { location: locationSetup.location.id } : undefined;
	}, [locationSetup?.location?.id]);

	const marketFilter = useMemo(() => {
		return { type: POS_TYPE.MARKET };
	}, []);

	const dueDateLabel = locationSetup?.dueDate ? moment(locationSetup.dueDate).format('DD MMM YYYY') : 'Not set';

	if (!locationSetup?.id) {
		return (
			<Screen>
				<EmptyState title="Select a location setup to create a market setup." />
			</Screen>
		);
	}

	return (
		<Screen
			heading="New market setup"
			subheading={locationSetup.identifier || 'Location setup'}
			dock={(
				<ActionDock
					action={{
						label: 'Create market setup',
						onPress: onSubmit,
						loading: submitting,
					}}
				/>
			)}
		>
			<SetupSection title="Location information">
				<SetupInfoRow
					label="Location"
					value={locationSetup.location?.name || 'Unknown'}
				/>
				<SetupInfoRow
					label="Client"
					value={locationSetup.location?.client?.name || 'Unknown'}
				/>
				<SetupInfoRow
					label="Due date"
					value={dueDateLabel}
				/>
			</SetupSection>

			<SetupSection title="Market details">
				<Text style={styles.label}>Place</Text>
				<PlaceSelector
					value={values.place}
					onChange={(place) => changeFormValue({ place })}
					initialFilter={placeFilter}
					buttonStyle={styles.selectorButton}
				/>

				<SetupCreatorPlacePhotos
					photos={values.placePhotos}
					onChange={placePhotos => changeFormValue({ placePhotos })}
					hint="Market space: take photo"
					error={errors?.placePhotos}
				/>

				<Text style={styles.label}>Market</Text>
				<MachineSelector
					value={values.machine}
					onChange={(machine) => changeFormValue({ machine })}
					initialFilter={marketFilter}
					buttonStyle={styles.selectorButton}
				/>

				<Text style={styles.label}>Planogram (optional)</Text>
				<PlanogramSelector
					value={values.planogram}
					onChange={(planogram) => changeFormValue({ planogram })}
					buttonStyle={styles.selectorButton}
				/>

				<SetupTextField
					label="Additional instructions"
					value={values.additionalInstructions}
					field="additionalInstructions"
					onConfirm={changeFormValue}
					multiline={true}
					numberOfLines={4}
					placeholder="Add any context for the team"
				/>
			</SetupSection>

			<SetupSection title="Assignees">
				<Text style={styles.label}>Assign users to this setup</Text>
				<SetupAssigneePicker
					assignees={values.assignees || []}
					onChange={assignees => changeFormValue({ assignees })}
					returnObjects={true}
					placeholder="Add assignee"
					initialFilter={{
						enabled: true,
						marketSetupAssigneeEligible: true,
					}}
				/>
			</SetupSection>
		</Screen>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	label: {
		...type.secondarySemibold,
		color: colors.text,
		marginBottom: 6,
		marginTop: space.md,
	},
	selectorButton: {
		width: 260,
	},
});

export default CreatorContainer(MarketSetupCreator);
